import { useCallback, useEffect, useState } from "react";
import { ApiError, getApi, ZOD_PARSE_ERROR } from "../../../../utils/api.ts";
import { accessToken } from "../../../../utils/auth.ts";
import { API_OFFBOARDING_LIST } from "../../../../../config/Endpoints.js";
import { useSqeLabs } from "../../../../hooks/use-sqe-labs.ts";
import { sqeOffboardingApi } from "../api/sqe-offboarding-api.ts";
import type { OffboardingSource } from "../types.ts";
import {
    OffboardingListItem,
    OffboardingListSchema,
    OffboardingStatus,
} from "../schemas/index.ts";

function normalizeOffboardingListPayload(data: unknown): unknown {
    if (data == null) return [];
    if (Array.isArray(data)) return data;

    if (typeof data === "object") {
        const record = data as Record<string, unknown>;
        if (Array.isArray(record.content)) return record.content;
        if (Array.isArray(record.data)) return record.data;
        if (Array.isArray(record.items)) return record.items;
        return [];
    }

    return [];
}

export function useOffboardingList({
    status,
    source,
}: { status?: OffboardingStatus; source?: OffboardingSource } = {}) {
    const [items, setItems] = useState<OffboardingListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const isSqe = useSqeLabs();
    const useSqeApi = source === "SQELABS" || (source == null && isSqe);

    const fetchList = useCallback(
        async (signal?: AbortSignal) => {
            setLoading(true);
            setError(null);

            try {
                if (useSqeApi) {
                    setItems(await sqeOffboardingApi.getList(status, signal));
                    return;
                }
                const url = status
                    ? `${API_OFFBOARDING_LIST}/${status}`
                    : API_OFFBOARDING_LIST;
                const { data } = await getApi<OffboardingListItem[]>(
                    url,
                    accessToken(),
                    "json",
                    signal,
                );

                if (!data) {
                    setItems([]);
                } else {
                    const normalized = normalizeOffboardingListPayload(data);
                    const parse = OffboardingListSchema.safeParse(normalized);

                    if (!parse.success) {
                        setItems([]);
                        setError(ZOD_PARSE_ERROR);
                    } else {
                        setItems(parse.data);
                    }
                }
            } catch (e) {
                const msg =
                    e instanceof ApiError
                        ? e.message
                        : "Failed to load offboarding list.";
                setError(msg);
                setItems([]);
            } finally {
                setLoading(false);
            }
        },
        [status, useSqeApi],
    );

    useEffect(() => {
        const ac = new AbortController();
        void fetchList(ac.signal);
        return () => ac.abort();
    }, [fetchList]);

    return { items, loading, error, refetch: fetchList };
}
